/* eslint-disable  */
import React, { useEffect, useState, useMemo, useRef } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { messageActionCreators, authActionCreators } from "../../services/Actions";
import RenderLoanPage from "../RenderLoanPage";
import Conversation from "./Conversation";
import moment from "moment";
import "./Loan.css";
import "./LoansMessages.css";
import { useHistory } from "react-router-dom";

function LoansMessages() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const { GetMessagesByDepartment, MarkMessageAsRead } = bindActionCreators(
    messageActionCreators,
    dispatch
  );
  const { GetUserProfile } = bindActionCreators(authActionCreators, dispatch);
  const auth = useSelector((state) => state?.auth);
  const message = useSelector((state) => state?.message);
  const { isLoading, messages, response } = message;

  console.log("messages", messages);

  // Filter and sort messages
  const filteredAndSortedMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];

    // First, sort messages by updated_at (newest first)
    const sortedMessages = [...messages].sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );

    // Then filter based on activeFilter
    let filteredMessages = sortedMessages;
    if (activeFilter === "unread") {
      filteredMessages = sortedMessages.filter((message) => {
        const chatArr = message?.chat;
        const last = chatArr?.at(-1);
        // Support both old (system_user_read) and new (is_read) fields
        const isRead = last?.system_user_read ?? last?.is_read ?? false;
        return !isRead;
      });
    } else if (activeFilter === "read") {
      filteredMessages = sortedMessages.filter((message) => {
        const chatArr = message?.chat;
        const last = chatArr?.at(-1);
        const isRead = last?.system_user_read ?? last?.is_read ?? false;
        return isRead;
      });
    }

    // Apply search filter if search term exists
    if (searchTerm) {
      filteredMessages = filteredMessages.filter((message) => {
        const staffId = message?.userData?.staff_id || message?.title || "";
        return staffId.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    return filteredMessages;
  }, [messages, activeFilter, searchTerm]);

  const allSelected =
    filteredAndSortedMessages.length > 0 &&
    selectedIds.length === filteredAndSortedMessages.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredAndSortedMessages.map((m) => m._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (e, id) => {
    e.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Count messages for filter badges
  const messageCounts = useMemo(() => {
    if (!messages || messages.length === 0) {
      return { all: 0, unread: 0, read: 0 };
    }

    const unreadCount = messages.filter((message) => {
      const chatArr = message?.chat;
      const last = chatArr?.at(-1);
      const isRead = last?.system_user_read ?? last?.is_read ?? false;
      return !isRead;
    }).length;

    return {
      all: messages.length,
      unread: unreadCount,
      read: messages.length - unreadCount,
    };
  }, [messages]);

  useEffect(() => {
    GetUserProfile();
    GetMessagesByDepartment("Loan Officer");
  }, []);

  // Socket.IO: join department room, receive list updates incrementally
  useEffect(() => {
    const socket = io("https://api.upnmg.com", {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    const joinRoom = () => {
      socket.emit("dept:join", { department: "Loan Officer" });
    };

    socket.on("connect", joinRoom);
    socket.on("reconnect", joinRoom);

    socket.on("dept:message_new", (update) => {
      dispatch({ type: "UPDATE_MESSAGES_LIST_ITEM", payload: update });
    });

    return () => {
      socket.emit("dept:leave", { department: "Loan Officer" });
      socket.disconnect();
    };
  }, []);

  return (
    <RenderLoanPage>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
            <p className="text-gray-600">
              Manage loan department communications
            </p>
          </div>

          {/* Filter Tabs and Search */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex space-x-1 p-1 bg-gray-100 rounded-lg w-fit">
              {[
                { key: "all", label: "All Messages", count: messageCounts.all },
                { key: "unread", label: "Unread", count: messageCounts.unread },
                { key: "read", label: "Read", count: messageCounts.read },
              ].map((filter) => (
                <button
                  key={filter.key}
                  onClick={() => setActiveFilter(filter.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                    activeFilter === filter.key
                      ? "bg-white text-green-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <span>{filter.label}</span>
                  {filter.count > 0 && (
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${
                        activeFilter === filter.key
                          ? "bg-blue-100 text-green-600"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {filter.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search by Staff ID */}
            <div className="relative w-full md:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search by Staff ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Bulk Action Bar */}
          {selectedIds.length > 0 && (
            <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-green-800">
                {selectedIds.length} message{selectedIds.length > 1 ? "s" : ""} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedIds([])}
                >
                  Clear
                </button>
                <button
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  onClick={() => {
                    selectedIds.forEach((id) => {
                      const msg = filteredAndSortedMessages.find((m) => m._id === id);
                      if (msg) {
                        const last = msg?.chat?.at(-1);
                        MarkMessageAsRead(id, last?._id);
                      }
                    });
                    setSelectedIds([]);
                  }}
                >
                  Mark as Read
                </button>
              </div>
            </div>
          )}

          {/* Messages Container */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-green-600 to-blue-700 px-4 py-4">
              <div className="grid grid-cols-12 gap-4 text-white font-semibold text-sm items-center">
                <div className="col-span-1 flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-white cursor-pointer accent-green-400"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={handleSelectAll}
                  />
                </div>
                <div className="col-span-3">Member Info</div>
                <div className="col-span-2">Reference</div>
                <div className="col-span-3">Subject</div>
                <div className="col-span-2">Department</div>
                <div className="col-span-1">Last Update</div>
              </div>
            </div>

            {/* Messages List */}
            <div className="divide-y divide-gray-200">
              {filteredAndSortedMessages?.length > 0 ? (
                filteredAndSortedMessages?.map((message, index) => {
                  let chatArr = message?.chat;
                  let last = chatArr?.at(-1);
                  console.log("last", last);
                  const readStatus = last?.system_user_read ?? last?.is_read ?? false;
                  let isUnread = !readStatus;

                  const isSelected = selectedIds.includes(message?._id);

                  return (
                    <div
                      key={message?._id}
                      className={`grid grid-cols-12 gap-4 px-4 py-5 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:shadow-md ${
                        isUnread
                          ? "bg-blue-50 border-l-4 border-l-green-500"
                          : ""
                      } ${isSelected ? "bg-green-50" : ""}`}
                      onClick={() => {
                        if (selectedIds.length > 0) return;
                        MarkMessageAsRead(message?._id, last?._id);
                        history.push({
                          pathname: "/loans/messages-detailed",
                          state: {
                            chat_id: message?._id,
                            userData: message?.userData,
                            reference: message?.reference,
                            subject: message?.title || message?.subject,
                            department: message?.system_user,
                          },
                        });
                      }}
                    >
                      {/* Checkbox */}
                      <div className="col-span-1 flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer accent-green-600"
                          checked={isSelected}
                          onChange={(e) => handleSelectOne(e, message?._id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Member Info */}
                      <div className="col-span-3 flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-gray-200 hover:ring-blue-400 transition-all duration-200">
                            {message?.userData?.profile_image ? (
                              <img
                                className="w-full h-full object-cover"
                                src={message?.userData?.profile_image}
                                alt={message?.userData?.name}
                              />
                            ) : (
                              <img
                                className="w-full h-full object-cover"
                                src="/images/dev/avarta2.jpeg"
                                alt={message?.userData?.name || message?.title}
                              />
                            )}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm font-medium truncate ${
                              isUnread ? "text-gray-900" : "text-gray-700"
                            }`}
                          >
                            {message?.userData?.name || message?.title || "Member"}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {message?.userData?.staff_id}
                          </p>
                        </div>
                      </div>

                      {/* Reference */}
                      <div className="col-span-2 flex items-center">
                        <div
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            isUnread
                              ? "bg-blue-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          #{message?.reference || message?._id?.slice(-5)}
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="col-span-3 min-w-0">
                        <p
                          className={`text-sm font-medium truncate mb-1 ${
                            isUnread ? "text-gray-900" : "text-gray-600"
                          }`}
                        >
                          {message?.title || message?.subject}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {last?.message}
                        </p>
                      </div>

                      {/* Department */}
                      <div className="col-span-2 flex items-center">
                        <div className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                          {message?.system_user || "Loan Officer"}
                        </div>
                      </div>

                      {/* Last Update */}
                      <div className="col-span-1 flex items-center justify-end">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {message?.updated_at && moment(message.updated_at).isValid() ? moment(message.updated_at).format("MMM DD") : "—"}
                          </p>
                          <p className="text-xs text-gray-400">
                            {message?.updated_at && moment(message.updated_at).isValid() ? moment(message.updated_at).format("YYYY") : ""}
                          </p>
                        </div>
                        {isUnread && (
                          <div className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-6 py-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {activeFilter === "all" && "No Messages Available"}
                    {activeFilter === "unread" && "No Unread Messages"}
                    {activeFilter === "read" && "No Read Messages"}
                    {searchTerm && "No matching messages found"}
                  </h3>
                  <p className="text-gray-500">
                    {activeFilter === "all" &&
                      !searchTerm &&
                      "Messages will appear here when they are received."}
                    {activeFilter === "unread" &&
                      !searchTerm &&
                      "All messages have been read."}
                    {activeFilter === "read" &&
                      !searchTerm &&
                      "No messages have been read yet."}
                    {searchTerm &&
                      "No messages match the staff ID you searched for."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className=" rounded-lg p-6 flex items-center space-x-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="text-gray-700 font-medium">
                  Loading messages...
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </RenderLoanPage>
  );
}

export default LoansMessages;
